import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import Button from "../components/common/Button";
import { isBrandRole } from "../config/portal";
import ContactFodxpert from "../components/common/ContactFodxpert";

const BRAND_NAV = [
  { id: "campaigns", name: "My Campaigns", route: "/campaigns" },
  { id: "creatives", name: "My Creatives", route: "/creatives" },
  { id: "reports", name: "Reports", route: "/reports" },
];

/**
 * Shell for the brand portal. Deliberately much thinner than AdminLayout:
 * a brand sees its own creatives and nothing else, so there is no permission-
 * driven navigation to build.
 */
const BrandLayout: React.FC = () => {
  const location = useLocation();
  const { profile, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // A tenant or client account that signs in on the brand hostname has no
  // brand-scoped data to show, so send it away rather than render an empty
  // portal that looks broken.
  if (profile && !isBrandRole(profile.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  const handleLogout = async () => {
    if (window.confirm("Are you sure you want to logout?")) {
      try {
        await logout();
      } catch (error) {
        // Logout error is surfaced by the logout mutation
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <header className="w-full h-[10vh] bg-background-dark text-white flex items-center justify-between px-6 shadow-md sticky top-0 z-50">
        <div className="flex items-center space-x-10">
          <Link to="/campaigns" className="flex items-center">
            <img
              src="/assets/images/logo.png"
              alt="Logo"
              className="h-8 w-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/logo192.png";
              }}
            />
          </Link>

          <nav className="flex space-x-6 text-md font-medium">
            {BRAND_NAV.map((item) => {
              const isActive = location.pathname.startsWith(item.route);
              return (
                <Link
                  key={item.id}
                  to={item.route}
                  className={`border-b-2 transition-colors ${
                    isActive
                      ? "border-white text-white"
                      : "border-transparent text-gray-300 hover:text-white"
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen((prev) => !prev)}
            className="h-10 w-10 rounded-full bg-primary-default text-white flex items-center justify-center font-bold text-lg shadow-md hover:opacity-90 transition"
          >
            {profile?.name?.[0]?.toUpperCase()}
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-60 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-4 animate-fade-in-up">
              <p className="text-base font-semibold text-gray-900">
                {profile?.name}
              </p>
              <p className="text-xs text-primary-default mb-3">Brand</p>
              <Link
                to="/profile"
                onClick={() => setDropdownOpen(false)}
                className="block w-full mb-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
              >
                View Profile
              </Link>
              <Button
                variant="danger"
                size="sm"
                onClick={handleLogout}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                Logout
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 w-full h-[90vh]">
        <Outlet />
      </main>

      <ContactFodxpert />
    </div>
  );
};

export default BrandLayout;
